import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PropTypes from "prop-types";

import LoggedInLayout from "../../layout/LoggedInLayout";
import { useToast } from "../../hook/useToast";
import { loadRazorpay } from "../../helper/loadRazorpay";
import {
  cancelCounsellingPayment,
  createCounsellingOrder,
  fetchCounsellorAvailability,
  fetchCounsellors,
  verifyCounsellingPayment,
} from "../../services/api";
import CouncilCard from "./CouncilCard";
import CouncilBookingDialog from "./CouncilBookingDialog";
import CouncilBookingSuccessDialog from "./CouncilBookingSuccessDialog";
import CouncilDetailDialog from "./CouncilDetailDialog";

const PAGE_SIZE = 8;
const NAVY = "#292E5C";
const ORANGE = "#F7AA47";
const BOOKING_TIMEZONE = "Asia/Kolkata";

const getCounsellorId = (counsellor) =>
  counsellor?.id || counsellor?.counsellor_id || null;

const getTodayISODate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.toISOString().slice(0, 10);
};

const searchInputSx = {
  flex: 1,
  "& .MuiOutlinedInput-root": {
    height: 52,
    borderRadius: "8px 0 0 8px",
    bgcolor: ORANGE,
    color: "#fff",
    fontFamily: "Montserrat, sans-serif",
    fontSize: 15,
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    "&::placeholder": {
      color: "#FFFEFF",
      opacity: 1,
    },
  },
};

const Council = ({ studentUserId, studentName, childSelector, counsellorType = "career" }) => {
  const isAdmission = counsellorType === "admission";
  const { showToast } = useToast();
  const [counsellors, setCounsellors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCounsellor, setSelectedCounsellor] = useState(null);
  const [bookingCounsellor, setBookingCounsellor] = useState(null);
  const [bookingConfirmation, setBookingConfirmation] = useState(null);
  const [bookingAvailabilityDays, setBookingAvailabilityDays] = useState([]);
  const [bookingAvailabilityLoading, setBookingAvailabilityLoading] = useState(false);
  const [bookingAvailabilityError, setBookingAvailabilityError] = useState("");
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  useEffect(() => {
    setSelectedCounsellor(null);
    setBookingCounsellor(null);
    setBookingConfirmation(null);
    setBookingAvailabilityDays([]);
    setBookingAvailabilityError("");
  }, [studentUserId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setCurrentPage(1);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const loadCounsellors = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetchCounsellors({
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearchTerm,
        counsellorType,
      });
      const payload = response.data?.data || {};

      setCounsellors(Array.isArray(payload.counsellors) ? payload.counsellors : []);
      setTotalPages(payload.pagination?.total_pages || 0);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        `Unable to load ${isAdmission ? "admission counsellors" : "counsellors"} right now.`;

      setError(message);
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, counsellorType, isAdmission, showToast]);

  useEffect(() => {
    loadCounsellors();
  }, [loadCounsellors]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setDebouncedSearchTerm(searchTerm.trim());
    setCurrentPage(1);
  };

  const handleSelectCounsellor = (counsellor) => {
    setBookingCounsellor(counsellor);
  };

  const handleViewCounsellorDetail = (counsellor) => {
    setSelectedCounsellor(counsellor);
  };

  const loadBookingAvailability = useCallback(async (counsellor) => {
    const counsellorId = getCounsellorId(counsellor);

    if (!counsellorId) {
      setBookingAvailabilityDays([]);
      setBookingAvailabilityError("Counsellor details are missing.");
      return;
    }

    setBookingAvailabilityLoading(true);
    setBookingAvailabilityError("");

    try {
      const response = await fetchCounsellorAvailability({
        counsellorId,
        startDate: getTodayISODate(),
        days: 7,
        timezone: BOOKING_TIMEZONE,
      });
      setBookingAvailabilityDays(response.data?.data?.days || []);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Unable to load available slots right now.";

      setBookingAvailabilityDays([]);
      setBookingAvailabilityError(message);
    } finally {
      setBookingAvailabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bookingCounsellor) {
      loadBookingAvailability(bookingCounsellor);
    } else {
      setBookingAvailabilityDays([]);
      setBookingAvailabilityError("");
      setBookingAvailabilityLoading(false);
      setBookingSubmitting(false);
    }
  }, [bookingCounsellor, loadBookingAvailability]);

  const handleBookSlot = async (booking) => {
    // Guard against a double-click while a payment is already in progress.
    if (bookingSubmitting) return;

    const counsellorId = getCounsellorId(booking.counsellor);

    if (!counsellorId || !booking.startAt) {
      setBookingCounsellor(null);
      setBookingConfirmation(booking);
      return;
    }

    setBookingSubmitting(true);

    try {
      const Razorpay = await loadRazorpay();

      const orderResponse = await createCounsellingOrder({
        counsellorId,
        startAt: booking.startAt,
        timezone: BOOKING_TIMEZONE,
        studentUserId,
      });
      const order = orderResponse.data?.data || {};
      const appointmentId = order.appointment_id;

      if (!appointmentId || !order.razorpay_order_id || !order.key_id) {
        throw new Error("Payment could not be initialised.");
      }

      const finishPending = () => {
        // Payment likely went through but we couldn't confirm synchronously —
        // don't tell the user to pay again; the webhook will reconcile it.
        setBookingCounsellor(null);
        setBookingConfirmation({
          ...booking,
          studentName,
          appointment: null,
          pendingVerification: true,
        });
        setBookingSubmitting(false);
      };

      const releaseHold = async () => {
        try {
          await cancelCounsellingPayment({ appointmentId, studentUserId });
        } catch (cancelErr) {
          console.error("Failed to release counselling hold", cancelErr);
        }
      };

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.razorpay_order_id,
        name: isAdmission ? "Admission Counselling Session" : "Counselling Session",
        description: `Session with ${order.counsellor_name || "counsellor"}`,
        prefill: {
          name: order.prefill?.name || studentName || "",
          email: order.prefill?.email || "",
          contact: order.prefill?.contact || "",
        },
        handler: async (response) => {
          try {
            const verifyResponse = await verifyCounsellingPayment({
              appointmentId,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              studentUserId,
            });

            if (verifyResponse.status === 202) {
              finishPending();
              return;
            }

            setBookingCounsellor(null);
            setBookingConfirmation({
              ...booking,
              studentName,
              appointment: verifyResponse.data?.data || null,
            });
          } catch (verifyErr) {
            const statusCode = verifyErr?.response?.status;
            if (statusCode === 400) {
              // Definitive rejection — payment could not be verified.
              const message =
                verifyErr?.response?.data?.message ||
                "Payment could not be verified.";
              showToast(message, "error");
              await loadBookingAvailability(booking.counsellor);
            } else {
              // Transient / network failure — leave it to the webhook.
              console.error("Payment verification error", verifyErr);
              finishPending();
            }
          } finally {
            setBookingSubmitting(false);
          }
        },
        modal: {
          ondismiss: async () => {
            await releaseHold();
            showToast("Payment cancelled.", "info");
            await loadBookingAvailability(booking.counsellor);
            setBookingSubmitting(false);
          },
        },
      };

      const checkout = new Razorpay(options);
      checkout.on("payment.failed", (response) => {
        const message =
          response?.error?.description ||
          "Payment failed. You can try again.";
        showToast(message, "error");
        // Re-enable "Confirm & Pay" so the user can retry after a failed payment.
        setBookingSubmitting(false);
      });
      checkout.open();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to start the payment right now.";

      console.error("Counselling payment flow error", err);
      showToast(message, "error");
      await loadBookingAvailability(booking.counsellor);
      setBookingSubmitting(false);
    }
  };

  return (
    <LoggedInLayout searchBar={false}>
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          bgcolor: "#fff",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2.5, md: 5 },
        }}
      >
        {childSelector}
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{
            width: "100%",
            display: "flex",
            mb: { xs: 3, md: 4 },
          }}
        >
          <TextField
            fullWidth
            value={searchTerm}
            placeholder={isAdmission ? "Search admission counsellor" : "Search counsellor"}
            onChange={(event) => setSearchTerm(event.target.value)}
            sx={searchInputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#FFFEFF", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            sx={{
              width: { xs: 92, sm: 120 },
              height: 52,
              borderRadius: "0 8px 8px 0",
              bgcolor: NAVY,
              color: "#FFFEFF",
              fontSize: 15,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              textTransform: "none",
              "&:hover": {
                bgcolor: "#20264F",
              },
            }}
          >
            Search
          </Button>
        </Box>

        <Box
          sx={{
            width: "100%",
            position: "relative",
            mb: 2.5,
            backgroundColor:"#f3f3f5",
              px:2,
              borderRadius:3
          }}
        >
          <Box
            sx={{
              minHeight: 46,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <SupportAgentIcon sx={{ width: 28, height: 28, color: NAVY }} />
              <Typography
                sx={{
                  color: "#231F20",
                  fontSize: 15,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                }}
              >
                Counselling
              </Typography>
            </Box>

            <Button
              type="button"
              variant="outlined"
              startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
              onClick={() => setIsFilterOpen((value) => !value)}
              sx={{
                height: 34,
                px: 1.25,
                py: 0.875,
                bgcolor: "#fff",
                borderColor: "#E2ECF3",
                borderRadius: "7px",
                color: "#363636",
                fontSize: 12,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 500,
                lineHeight: "15px",
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#fff",
                  borderColor: "#D6DFE8",
                },
              }}
            >
              Filter
            </Button>
          </Box>

          {isFilterOpen ? (
            <Paper
              elevation={0}
              sx={{
                position: { xs: "static", md: "absolute" },
                right: 0,
                top: 46,
                zIndex: 4,
                width: { xs: "100%", sm: 221 },
                mt: { xs: 1.5, md: 0 },
                p: 2,
                bgcolor: "#F7F7F7",
                borderRadius: "8px",
                boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.06)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    color: "#111827",
                    fontSize: 12,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  Filter Now
                </Typography>
                <Button
                  type="button"
                  aria-label="Close filters"
                  onClick={() => setIsFilterOpen(false)}
                  sx={{
                    minWidth: 0,
                    width: 24,
                    height: 24,
                    p: 0,
                    color: "#6B7280",
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </Button>
              </Box>

              <FormControl fullWidth size="small" disabled>
                <Select
                  value=""
                  displayEmpty
                  sx={{
                    height: 36,
                    borderRadius: "7px",
                    bgcolor: "#fff",
                    color: "#888888",
                    fontSize: 12,
                    fontFamily: "Montserrat, sans-serif",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#DCDFDE",
                    },
                  }}
                >
                  <MenuItem value="">-- Select City --</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                <Button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#374151",
                    fontSize: 12,
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    lineHeight: "14px",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "#fff",
                      borderColor: "#D1D5DB",
                    },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Paper>
          ) : null}
        </Box>

        <Typography
          sx={{
            color: "#231F20",
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
            lineHeight: "31.5px",
            mb: 3,
          }}
        >
          Here is the list of the best counsellors.
          <br />
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 400,
              lineHeight: "31.5px",
            }}
          >
            Select your mentor to decode your report, gain a brief understanding,
            and engage in a one-to-one discussion.
          </Box>
        </Typography>

        {error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadCounsellors}>
                Retry
              </Button>
            }
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: NAVY }} />
          </Box>
        ) : null}

        {!loading && !error ? (
          <>
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: { xs: "12px", md: "20px" },
              }}
            >
              {counsellors.map((counsellor) => (
                <CouncilCard
                  key={counsellor.id || counsellor.counsellor_id}
                  counsellor={counsellor}
                  onDetail={handleViewCounsellorDetail}
                  onSelect={handleSelectCounsellor}
                />
              ))}
            </Box>

            {counsellors.length === 0 ? (
              <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 7 }}>
                <Typography
                  sx={{
                    color: "#231F20",
                    fontSize: 15,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {debouncedSearchTerm
                    ? `No counsellors found for "${debouncedSearchTerm}".`
                    : "No counsellors available right now."}
                </Typography>
              </Box>
            ) : null}

            {totalPages > 1 ? (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", md: "flex-end" },
                  gap: 2,
                  mt: 3,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, value) => setCurrentPage(value)}
                  siblingCount={1}
                  boundaryCount={1}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      width: 32,
                      height: 32,
                      borderRadius: "4px",
                      border: "0.55px solid rgba(92, 90, 90, 0.5)",
                      color: "#000",
                      fontSize: 14,
                      fontFamily: "DM Sans, sans-serif",
                      fontWeight: 500,
                      lineHeight: "20px",
                      backgroundColor:"#fff"
                    },
                    "& .MuiPaginationItem-root.Mui-selected": {
                      bgcolor: "#fff",
                      border: `1px solid #F7AA47`,
                      color: "#F7AA47",
                    },
                    "& .MuiPaginationItem-root.Mui-selected:hover": {
                      bgcolor: "#fff",
                    },
                  }}
                />
                
              </Box>
            ) : null}
          </>
        ) : null}

        <CouncilDetailDialog
          open={Boolean(selectedCounsellor)}
          counsellor={selectedCounsellor}
          onClose={() => setSelectedCounsellor(null)}
        />
        <CouncilBookingDialog
          open={Boolean(bookingCounsellor)}
          counsellor={bookingCounsellor}
          availabilityDays={bookingAvailabilityDays}
          loading={bookingAvailabilityLoading}
          error={bookingAvailabilityError}
          bookingLoading={bookingSubmitting}
          onClose={() => setBookingCounsellor(null)}
          onBookSlot={handleBookSlot}
          onRetry={() => loadBookingAvailability(bookingCounsellor)}
        />
        <CouncilBookingSuccessDialog
          open={Boolean(bookingConfirmation)}
          booking={bookingConfirmation}
          onClose={() => setBookingConfirmation(null)}
        />
      </Box>
    </LoggedInLayout>
  );
};

Council.propTypes = {
  studentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studentName: PropTypes.string,
  childSelector: PropTypes.node,
  counsellorType: PropTypes.oneOf(["career", "admission"]),
};

export default Council;
